/**
 * 🎯 Admin Filter Management Page
 * Complete CRUD interface for managing filter options
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  TableSortLabel,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  FilterList,
  Analytics,
  Publish,
  Unpublished,
  Schedule,
  DragIndicator,
} from '@mui/icons-material';
import { filterManagementService } from '@/services/filterManagementService';
import {
  FilterOption,
  FilterCategory,
  FilterStatus,
  FilterSortField,
  SortOrder,
  ListFilterOptionsParams,
} from '@/types/filters';
import { CreateFilterModal } from '@/components/admin/CreateFilterModal';
import { FilterAnalytics } from '@/components/admin/FilterAnalytics';
import toast from 'react-hot-toast';

// ============================================
// Helper Functions
// ============================================

const getStatusColor = (status: FilterStatus): 'default' | 'success' | 'warning' | 'info' | 'error' => {
  switch (status) {
    case FilterStatus.PUBLISHED:
      return 'success';
    case FilterStatus.SCHEDULED:
      return 'info';
    case FilterStatus.DRAFT:
      return 'warning';
    case FilterStatus.UNPUBLISHED:
      return 'error';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: FilterStatus): string => {
  switch (status) {
    case FilterStatus.PUBLISHED:
      return 'Published';
    case FilterStatus.SCHEDULED:
      return 'Scheduled';
    case FilterStatus.DRAFT:
      return 'Draft';
    case FilterStatus.UNPUBLISHED:
      return 'Unpublished';
    default:
      return status;
  }
};

const getStatusIcon = (status: FilterStatus) => {
  switch (status) {
    case FilterStatus.PUBLISHED:
      return <Publish fontSize="small" />;
    case FilterStatus.SCHEDULED:
      return <Schedule fontSize="small" />;
    case FilterStatus.DRAFT:
      return <Edit fontSize="small" />;
    case FilterStatus.UNPUBLISHED:
      return <Unpublished fontSize="small" />;
    default:
      return null;
  }
};

// ============================================
// Main Component
// ============================================

export const FilterManagementPage: React.FC = () => {
  // State: Data
  const [filters, setFilters] = useState<FilterOption[]>([]);
  const [categories, setCategories] = useState<FilterCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  // State: Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // State: Filtering
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<FilterStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [featuredFilter, setFeaturedFilter] = useState<boolean | ''>('');

  // State: Sorting
  const [sortBy, setSortBy] = useState<FilterSortField>('display_order');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // State: Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<FilterOption | null>(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);

  // ============================================
  // Data Loading
  // ============================================

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadFilters();
  }, [page, rowsPerPage, searchQuery, statusFilter, categoryFilter, featuredFilter, sortBy, sortOrder]);

  const loadCategories = async () => {
    try {
      const response = await filterManagementService.listFilterCategories();
      if (response.success && response.data) {
        const categoryData = response.data.categories || [];
        setCategories(categoryData);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
      setCategories([]);
    }
  };

  const loadFilters = async () => {
    setLoading(true);
    try {
      const params: ListFilterOptionsParams = {
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.categoryId = categoryFilter;
      if (featuredFilter !== '') params.is_featured = featuredFilter;

      const response = await filterManagementService.listFilterOptions(params);

      if (response.success && response.data) {
        const filterData = Array.isArray(response.data) ? response.data : (response.data.filters || []);
        const paginationData = response.pagination || response.data.pagination;

        setFilters(filterData);
        setTotalCount(paginationData?.total || paginationData?.total_count || 0);
      } else {
        setFilters([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Failed to load filters:', error);
      setFilters([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // Event Handlers
  // ============================================

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field: FilterSortField) => {
    const isAsc = sortBy === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(field);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedFilters(filters.map((f) => f.id));
    } else {
      setSelectedFilters([]);
    }
  };

  const handleSelectOne = (id: string) => {
    setSelectedFilters((prev) =>
      prev.includes(id) ? prev.filter((fId) => fId !== id) : [...prev, id]
    );
  };

  const handleEdit = (filter: FilterOption) => {
    setEditingFilter(filter);
    setCreateModalOpen(true);
  };

  const handleDelete = async (filter: FilterOption) => {
    if (!confirm(`Are you sure you want to delete "${filter.name}"? This action can be undone (soft delete).`)) {
      return;
    }

    try {
      const response = await filterManagementService.deleteFilterOption(filter.id);
      if (response.success) {
        toast.success('Filter option deleted successfully');
        loadFilters();
      } else {
        toast.error(response.error || 'Failed to delete filter option');
      }
    } catch (error: any) {
      console.error('Failed to delete filter option:', error);
      toast.error('Failed to delete filter option');
    }
  };

  const handleBulkPublish = async () => {
    if (selectedFilters.length === 0) {
      toast.error('No filters selected');
      return;
    }

    try {
      const response = await filterManagementService.bulkPublish(selectedFilters);
      if (response.success) {
        toast.success(`${selectedFilters.length} filter(s) published successfully`);
        setSelectedFilters([]);
        loadFilters();
      } else {
        toast.error(response.error || 'Failed to publish filters');
      }
    } catch (error: any) {
      console.error('Failed to bulk publish:', error);
      toast.error('Failed to publish filters');
    }
  };

  const handleBulkUnpublish = async () => {
    if (selectedFilters.length === 0) {
      toast.error('No filters selected');
      return;
    }

    try {
      const response = await filterManagementService.bulkUnpublish(selectedFilters);
      if (response.success) {
        toast.success(`${selectedFilters.length} filter(s) unpublished successfully`);
        setSelectedFilters([]);
        loadFilters();
      } else {
        toast.error(response.error || 'Failed to unpublish filters');
      }
    } catch (error: any) {
      console.error('Failed to bulk unpublish:', error);
      toast.error('Failed to unpublish filters');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedFilters.length === 0) {
      toast.error('No filters selected');
      return;
    }

    if (!confirm(`Are you sure you want to delete ${selectedFilters.length} filter option(s)?`)) {
      return;
    }

    try {
      const response = await filterManagementService.bulkDelete(selectedFilters);
      if (response.success) {
        toast.success(`${selectedFilters.length} filter(s) deleted successfully`);
        setSelectedFilters([]);
        loadFilters();
      } else {
        toast.error(response.error || 'Failed to delete filters');
      }
    } catch (error: any) {
      console.error('Failed to bulk delete:', error);
      toast.error('Failed to delete filters');
    }
  };

  const getCategoryName = (filter: any): string => {
    // Backend returns category as a nested object
    if (filter.category && typeof filter.category === 'object') {
      return filter.category.name || filter.category.categoryName || 'Unknown';
    }

    // Use categoryName from filter directly
    if (filter.categoryName) {
      return filter.categoryName;
    }

    // Use categoryKey as a fallback and format it nicely
    if (filter.categoryKey) {
      return filter.categoryKey
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    // Look up category by ID from categories array
    const categoryId = filter.categoryId || filter.category_id;
    if (categoryId && categories.length > 0) {
      const category = categories.find((c) => c.id === categoryId);
      if (category?.categoryName) {
        return category.categoryName;
      }
    }

    return 'Unknown';
  };

  // ============================================
  // Render
  // ============================================

  if (loading && filters.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Filter Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage filter options for search, booking, and discovery features
        </Typography>
      </Box>

      {/* Action Bar */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          {/* Search */}
          <TextField
            placeholder="Search filters..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />

          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value as FilterStatus | '')}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      color: 'text.primary',
                    },
                  },
                },
              }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value={FilterStatus.PUBLISHED}>Published</MenuItem>
              <MenuItem value={FilterStatus.DRAFT}>Draft</MenuItem>
              <MenuItem value={FilterStatus.SCHEDULED}>Scheduled</MenuItem>
              <MenuItem value={FilterStatus.UNPUBLISHED}>Unpublished</MenuItem>
            </Select>
          </FormControl>

          {/* Category Filter */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: '#1a1a1a',
                    '& .MuiList-root': {
                      bgcolor: '#1a1a1a',
                    },
                    '& .MuiMenuItem-root': {
                      color: '#ffffff !important',
                      bgcolor: '#2a2a2a',
                      borderBottom: '1px solid #3a3a3a',
                      '&:hover': {
                        backgroundColor: '#3a3a3a !important',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#4a4a4a !important',
                        '&:hover': {
                          backgroundColor: '#5a5a5a !important',
                        },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="" sx={{ color: '#ffffff !important' }}>All Categories</MenuItem>
              {categories.map((cat) => {
                return (
                  <MenuItem
                    key={cat.id}
                    value={cat.id}
                    sx={{
                      color: '#ffffff !important',
                      backgroundColor: '#2a2a2a',
                      '&:hover': {
                        backgroundColor: '#3a3a3a !important',
                      },
                    }}
                  >
                    {cat.categoryName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {/* Featured Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Featured</InputLabel>
            <Select
              value={featuredFilter}
              label="Featured"
              onChange={(e) => setFeaturedFilter(e.target.value as boolean | '')}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      color: 'text.primary',
                    },
                  },
                },
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Featured Only</MenuItem>
              <MenuItem value="false">Non-Featured</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ flexGrow: 1 }} />

          {/* Action Buttons */}
          <Button
            variant="outlined"
            startIcon={<Analytics />}
            onClick={() => setAnalyticsOpen(true)}
          >
            Analytics
          </Button>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditingFilter(null);
              setCreateModalOpen(true);
            }}
          >
            Create Filter
          </Button>
        </Stack>

        {/* Bulk Actions */}
        {selectedFilters.length > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {selectedFilters.length} selected
              </Typography>
              <Button size="small" startIcon={<Publish />} onClick={handleBulkPublish}>
                Publish
              </Button>
              <Button size="small" startIcon={<Unpublished />} onClick={handleBulkUnpublish}>
                Unpublish
              </Button>
              <Button size="small" startIcon={<Delete />} color="error" onClick={handleBulkDelete}>
                Delete
              </Button>
            </Stack>
          </Box>
        )}
      </Card>

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedFilters.length === filters.length && filters.length > 0}
                    indeterminate={selectedFilters.length > 0 && selectedFilters.length < filters.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'display_order'}
                    direction={sortBy === 'display_order' ? sortOrder : 'asc'}
                    onClick={() => handleSort('display_order')}
                  >
                    Order
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'name'}
                    direction={sortBy === 'name' ? sortOrder : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'category'}
                    direction={sortBy === 'category' ? sortOrder : 'asc'}
                    onClick={() => handleSort('category')}
                  >
                    Category
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'status'}
                    direction={sortBy === 'status' ? sortOrder : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'usage_count'}
                    direction={sortBy === 'usage_count' ? sortOrder : 'asc'}
                    onClick={() => handleSort('usage_count')}
                  >
                    Usage
                  </TableSortLabel>
                </TableCell>
                <TableCell>Featured</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filters.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Typography variant="body2" color="text.secondary">
                      No filter options found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filters.map((filter) => (
                  <TableRow
                    key={filter.id}
                    hover
                    selected={selectedFilters.includes(filter.id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedFilters.includes(filter.id)}
                        onChange={() => handleSelectOne(filter.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <DragIndicator fontSize="small" sx={{ color: 'text.disabled', cursor: 'move' }} />
                        <Typography variant="body2">{filter.displayOrder || filter.display_order || 0}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack>
                        <Typography variant="body2" fontWeight={500}>
                          {filter.optionLabel || filter.name || 'N/A'}
                        </Typography>
                        {(filter.optionDescription || filter.description) && (
                          <Typography variant="caption" color="text.secondary">
                            {filter.optionDescription || filter.description}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        {getCategoryName(filter)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(filter.status)}
                        label={getStatusLabel(filter.status)}
                        size="small"
                        color={getStatusColor(filter.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {(filter.analytics?.usageCount || filter.usage_count || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(filter.isFeatured || filter.is_featured) && (
                        <Chip label="Featured" size="small" color="primary" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(filter)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(filter)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      {/* Modals */}
      <CreateFilterModal
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setEditingFilter(null);
        }}
        onSuccess={loadFilters}
        editingFilter={editingFilter}
        categories={categories}
      />

      <FilterAnalytics
        open={analyticsOpen}
        onClose={() => setAnalyticsOpen(false)}
        categories={categories}
      />
    </Box>
  );
};

export default FilterManagementPage;
